import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';

/** The kiosk PWA's manifest scope — see `public/manifest.kiosk.webmanifest`. */
const KIOSK_SCOPE = '/kiosk';

/** True for `/kiosk` itself or a path beneath it — never `/kiosks`, never `/kiosk-anything`. */
const isKioskScope = (pathname: string): boolean =>
  pathname === KIOSK_SCOPE || pathname.startsWith(`${KIOSK_SCOPE}/`);

/**
 * Locks the app to the `/kiosk` route tree, but ONLY for the kiosk PWA. Once installed and
 * launched from the home screen the kiosk should behave like a single-purpose appliance: deep
 * links, the SPA's default redirects, or a stray navigation must not pop it into the main web app.
 *
 * Two signals must agree, and both are load-bearing:
 *
 * 1. **Installed** — running in standalone/fullscreen display mode. In a regular browser tab the
 *    guard is a no-op, so an admin can open `/kiosk` to pair a device and then navigate away.
 * 2. **Launched inside `/kiosk`** — the document URL sits in the kiosk manifest's scope. Display
 *    mode alone is NOT sufficient: `manifest.webmanifest` is also `display: standalone`, so keying
 *    off display mode alone redirected the *main* H4C PWA to `/kiosk` as well, which left it
 *    unusable once installed. The two manifests are only distinguishable by their `scope` /
 *    `start_url` (`/kiosk` vs `/`) — the same `location.pathname` test `PwaBrandingService` uses to
 *    keep its runtime branding off the kiosk's manifest.
 *
 * Reading `location.pathname` on every call is safe here: the router's default `urlUpdateStrategy`
 * is `'deferred'`, so while a `canMatch` guard evaluates, the address bar still holds the URL the
 * kiosk is currently on. A session that launched at `/kiosk` therefore can never leave it, while
 * the main PWA — which launches at `/` — is never captured.
 *
 * Intended to be wired as `canMatch` on every non-kiosk top-level route.
 */
export const kioskPwaScopeGuard: CanMatchFn = () => {
  if (typeof window === 'undefined') return true;

  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    // iOS Safari exposes the legacy navigator.standalone flag instead of the display-mode query.
    !!(navigator as Navigator & { standalone?: boolean }).standalone;

  if (!standalone) return true;

  // An installed PWA that did not launch inside /kiosk is the main app, not the kiosk — let it be.
  if (!isKioskScope(window.location.pathname)) return true;

  return inject(Router).parseUrl(KIOSK_SCOPE);
};
